import React, { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { getAPIBaseURL } from "../../services/helpers"
import { getData } from "../../services/request"

const statusFilters = [
  { key: "ALL", label: "All" },
  { key: "open", label: "Open" },
  { key: "resolved", label: "Resolved" }
]

const AdminDisputes = () => {
  const [disputes, setDisputes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [query, setQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState("ALL")

  const fetchDisputes = async () => {
    setLoading(true)
    setError("")
    const url = `${getAPIBaseURL()}/v1/disputes`
    const params = { limit: 200, skip: 0 }
    
    if (statusFilter && statusFilter !== 'ALL') {
      params.status = statusFilter
    }

    const res = await getData(url, {}, params)
    setLoading(false)
    if (res.error == "") {
      if (res.result.status === 200) {
        setDisputes(res.result.data?.data || [])
      } else {
        setError(res.result.data?.message || 'Failed to load disputes')
      }
    } else {
      setError(res.result?.data?.message || 'Network error')
    }
  }

  useEffect(() => {
    fetchDisputes()
  }, [statusFilter])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return disputes
    
    return disputes.filter(d => {
      const subject = d.subject || ''
      const message = d.message || ''
      const createdByName = d.createdBy?.name || d.createdBy?.organization || ''
      const postMessage = d.postId?.message || ''
      
      return (
        subject.toLowerCase().includes(q) ||
        message.toLowerCase().includes(q) ||
        createdByName.toLowerCase().includes(q) ||
        postMessage.toLowerCase().includes(q)
      )
    })
  }, [disputes, query])

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString()
    } catch (e) {
      return iso
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'resolved':
        return 'bg-green-100 text-green-700'
      case 'open':
        return 'bg-yellow-100 text-yellow-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  const getRoleColor = (role) => {
    switch (role) {
      case 'HOSPITAL':
        return 'bg-blue-100 text-blue-700'
      case 'PATIENT':
        return 'bg-purple-100 text-purple-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  return (
    <section className="mx-auto max-w-6xl">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Disputes</h1>
          <p className="text-sm text-gray-600">Review and resolve user disputes</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search disputes..."
            className="w-72 max-w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
          />
        </div>
      </div>

      {/* Status Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {statusFilters.map((filter) => (
          <button
            key={filter.key}
            onClick={() => setStatusFilter(filter.key)}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors sm:text-sm ${
              statusFilter === filter.key
                ? 'border-black bg-black text-white'
                : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <div className="p-6 text-sm text-gray-500">Loading...</div>
        ) : error ? (
          <div className="p-6 text-sm text-red-600">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No disputes found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 font-semibold text-gray-700">Created By</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Subject</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Date</th>
                  <th className="px-4 py-3 font-semibold text-gray-700"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((dispute) => (
                  <tr key={dispute._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">
                        {dispute.createdBy?.organization || dispute.createdBy?.name || 'Unknown'}
                      </div>
                      <div className="mt-1">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${getRoleColor(dispute.createdByRole)}`}>
                          {dispute.createdByRole}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="max-w-xs truncate font-medium text-gray-900" title={dispute.subject}>
                        {dispute.subject}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusColor(dispute.status)}`}>
                        {dispute.status?.toUpperCase() || 'UNKNOWN'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatDate(dispute.createdAt)}</td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/admin/app/disputes/${dispute._id}`}
                        className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  )
}

export default AdminDisputes

